import { zodResolver } from '@hookform/resolvers/zod'
import { getRouteApi } from '@tanstack/react-router'
import { type DefaultValues, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'

import Loading from '@/components/Loading'
import BasicLayout from '@/components/common/BasicLayout/BasicLayout'
import useExternalErrors from '@/hooks/useExternalErrors'
import { toast } from '@/hooks/useToast'
import { useAllEducationLevels } from '@/queries/useAllEducationLevels'

import CourseForm from '../components/CourseForm'
import {
  courseFormPayload,
  type CourseFormPayload
} from '../constants/course-payload'
import useAddCourse from '../mutations/useAddCourse'
import { getFullNamePreview } from '../utils/get-full-name-preview'

const defaultValues: DefaultValues<CourseFormPayload> = {
  educationLevel: undefined,
  name: '',
  domain: undefined,
  preferredRooms: []
}

const routeApi = getRouteApi('/_auth/courses-management/add')

const CourseAddView = () => {
  const { t } = useTranslation(['coursesManagement', 'common'])
  const navigate = routeApi.useNavigate()
  const { data: allEducationLevels, isLoading: isAllEducationLevelsLoading } =
    useAllEducationLevels()

  const form = useForm<CourseFormPayload>({
    resolver: zodResolver(courseFormPayload()),
    mode: 'all',
    defaultValues
  })

  const {
    mutate: addCourse,
    isPending: isAddCoursePending,
    formErrors
  } = useAddCourse({
    onSuccess: ({ response, variables }) => {
      toast({
        variant: 'success',
        title: t('toast.course-add-success', {
          COURSE: getFullNamePreview({
            name: variables.name,
            educationLevel:
              allEducationLevels?.map?.[variables.educationLevel]?.shortName
          })
        })
      })
      navigate({
        to: '/courses-management/$courseId',
        params: {
          courseId: response.id
        }
      })
    },
    onError: (_, variables) => {
      toast({
        variant: 'error',
        title: t('toast.course-add-error', {
          COURSE: getFullNamePreview({
            name: variables.name,
            educationLevel:
              allEducationLevels?.map?.[variables.educationLevel]?.shortName
          })
        })
      })
    }
  })
  useExternalErrors(formErrors, form)

  const isLoading = isAllEducationLevelsLoading || isAddCoursePending

  return (
    <BasicLayout
      breadcrumbs={[
        {
          title: t('common:navigation.courses-management'),
          to: '/courses-management'
        },
        {
          title: t('header.add-new-course')
        }
      ]}
      header={t('header.add-new-course')}
      moduleName={t('common:header.administration')}
    >
      <Loading spinning={isLoading}>
        <CourseForm form={form} onSubmit={addCourse} />
      </Loading>
    </BasicLayout>
  )
}

export default CourseAddView
